import { ContentItem } from '@thg-commerce/enterprise-components'

declare global {
  interface Window {
    dataLayer: any[]
  }
}

interface TrackingEvent {
  type: 'elysiumEvent' | 'customEvent' | 'marketing_preference'
  eventData: {
    eventAction: string
    eventCategory: string
    eventLabel?: string
    eventLabelValue?: string
    eventPage?: string
  }
}

interface DataLayerGA4 {
  event: string
  event_name: string
  ecommerce?: {
    currencyCode: string
    items: [
      {
        item_name?: string
        item_id?: string | number
        price?: string | number
        item_brand?: string | { name: string }
        item_category?: string
        quantity?: number
      },
    ]
  }
  coupon_code?: string
}

export interface EnhancedTrackingEvent {
  event: string
}

enum FulfilmentMethodDataLayer {
  HomeDelivery = 'HOME_DELIVERY',
  CollectInStore = 'COLLECT_IN_STORE',
  DeliverToStore = 'DELIVER_TO_STORE',
  CollectFromStore = 'COLLECT_FROM_STORE',
  Warehouse = 'WAREHOUSE',
}

export const pushToDataLayer = (event: TrackingEvent) => {
  if (!window) {
    return
  }
  if (!window.dataLayer) {
    window.dataLayer = []
  }

  window.dataLayer.push({
    event: event.type,
    eventData: {
      ...event.eventData,
      eventPage: window.location.pathname,
    },
  })
}

export const pushToEnhancedDataLayer = <T extends EnhancedTrackingEvent>(
  event: T,
) => {
  if (!window) {
    return
  }

  if (!window.dataLayer) {
    window.dataLayer = []
  }
  window.dataLayer.push(event)
}

export const pushToEventGA4 = (dataLayer: DataLayerGA4) => {
  if (!window) {
    return
  }

  window.dataLayer.push(dataLayer)
}

export const pushProductViewData = (
  productName,
  externalSku,
  productPrice,
  productBrand,
  inStockWarehouse,
  homeDeliveryAvailable,
  clickAndCollect,
  checkStock,
  noCheckStock,
  collectionType,
  category,
  range,
  productCollectionType,
) => {
  const isInStockWarehouse = inStockWarehouse?.includes(
    FulfilmentMethodDataLayer.Warehouse,
  )
    ? 'in'
    : 'out'

  let hasHomeDeliveryAvailable = 'Not available for delivery'
  let isCheckStock = 'No check stock'
  let fulfilment = 'Not Available'

  if (
    inStockWarehouse?.includes(FulfilmentMethodDataLayer.Warehouse) &&
    homeDeliveryAvailable
  ) {
    hasHomeDeliveryAvailable = 'In Stock'
  } else if (
    homeDeliveryAvailable &&
    !inStockWarehouse?.includes(FulfilmentMethodDataLayer.Warehouse)
  ) {
    hasHomeDeliveryAvailable = 'Out of stock'
  }

  if (clickAndCollect || checkStock) {
    isCheckStock = 'Can check stock'
  }

  if (clickAndCollect) {
    fulfilment = 'Available to order'
  } else if (checkStock) {
    fulfilment = 'Check stock'
  } else if (noCheckStock) {
    fulfilment = 'No check stock'
  }

  const product = {
    category,
    range,
    name: productName,
    id: externalSku,
    price: productPrice,
    brand: productBrand,
    dimension48: isInStockWarehouse,
    'Home Delivery': hasHomeDeliveryAvailable,
    'In-Store': fulfilment,
    dimension46: `${homeDeliveryAvailable}`,
    dimension45: productCollectionType ? productCollectionType : collectionType,
  }

  if (inStockWarehouse && homeDeliveryAvailable) {
    product['Home deliverable'] = isCheckStock
  } else {
    product['Home deliverable'] = isCheckStock
  }

  return product
}

const getFulfilmentType = (type) => {
  if (type === FulfilmentMethodDataLayer.CollectInStore) {
    return FulfilmentMethodDataLayer.CollectFromStore
  }

  return type
}

export const pushFulfilmentData = (
  inStockWarehouse,
  homeDeliveryAvailable,
  clickAndCollectAvailable,
  fulfilmentType,
  productCollectionType,
) => {
  const isInStockWarehouse = inStockWarehouse?.includes(
    FulfilmentMethodDataLayer.Warehouse,
  )
    ? 'in'
    : 'out'

  const isHomeDeliveryAvailable = String(homeDeliveryAvailable)

  const collectionType = clickAndCollectAvailable
    ? 'click_and_collect'
    : 'order_in_store'

  return {
    dimension3: getFulfilmentType(fulfilmentType),
    dimension45: productCollectionType ? productCollectionType : collectionType,
    dimension46: isHomeDeliveryAvailable,
    dimension48: isInStockWarehouse,
  }
}
// @TODO CSBOM-411 Move logic server side
const getTagFromContent = (
  productDescriptionKey: string,
  content: ContentItem[] | undefined,
): string | null => {
  const contentFromContent = content?.find(
    ({ key }) => key === productDescriptionKey,
  )
  // @TODO CSBOM-413 Remove casting for DL changes
  if (contentFromContent) {
    const contentValue = contentFromContent.value as {
      __typename: 'ProductContentStringValue'
      stringValue: string
    }
    return contentValue.stringValue
  }

  return null
}

export const productCategoryString = (
  content: ContentItem[] | undefined,
  productCategoryKeys: string[] | undefined,
) => {
  let categoryString: string = ''
  const slash = '/'
  if (productCategoryKeys) {
    productCategoryKeys.map((key) => {
      const tag = getTagFromContent(key, content)
      if (tag) {
        if (categoryString.length > 0) {
          categoryString += slash
        }
        categoryString += tag
      }
    })
    return categoryString
  }
  return ''
}

export const getValueFromKey = (
  content: ContentItem[] | undefined,
  productCategoryKey: string | undefined,
) => {
  if (content !== undefined && productCategoryKey !== undefined) {
    const categoryItem = content?.find(({ key }) => key === productCategoryKey)
    if (categoryItem) {
      const categoryItemValue = categoryItem.value

      if (
        categoryItemValue.stringListValue &&
        categoryItemValue.stringListValue.length > 0
      ) {
        return categoryItemValue.stringListValue[0]
      }
    }
    return
  }
  return
}
